import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * F26 — Hook admin per panoramica catalogo:
 *  - Tutti gli addon (anche non-active) raggruppati per brand
 *  - Conteggio reale materials/strat per starter_category
 *  - Lista entitlement attivi con org name
 *  - Revenue stimata (entitlement count × price_eur)
 */

export interface AdminAddon {
  id: string;
  slug: string;
  name: string;
  brand_slug: string | null;
  category: string;
  version_year: number | null;
  is_current_year: boolean;
  is_active: boolean;
  price_eur: number;
  upgrade_price_eur: number | null;
  starter_category_filter: string | null;
  preview_count_materials: number;
  preview_count_stratigraphies: number;
  lemonsqueezy_variant_id: string | null;
  includes_addon_slugs: string[] | null;
}

export interface AdminEntitlement {
  id: string;
  organization_id: string;
  organization_name: string;
  addon_id: string;
  addon_slug: string;
  addon_name: string;
  purchased_at: string;
  lemonsqueezy_order_id: string | null;
}

export function useAdminAddons() {
  return useQuery<AdminAddon[]>({
    queryKey: ['admin', 'addons'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('library_addons')
        .select('*')
        .order('brand_slug', { ascending: true })
        .order('category', { ascending: true })
        .order('version_year', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AdminAddon[];
    },
  });
}

export function useAdminEntitlements() {
  return useQuery<AdminEntitlement[]>({
    queryKey: ['admin', 'entitlements'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_library_entitlements')
        .select(`
          id,
          organization_id,
          addon_id,
          purchased_at,
          lemonsqueezy_order_id,
          organizations(name),
          library_addons(slug, name)
        `)
        .order('purchased_at', { ascending: false });
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []).map((row: any) => ({
        id: row.id,
        organization_id: row.organization_id,
        organization_name: row.organizations?.name ?? '—',
        addon_id: row.addon_id,
        addon_slug: row.library_addons?.slug ?? '',
        addon_name: row.library_addons?.name ?? '',
        purchased_at: row.purchased_at,
        lemonsqueezy_order_id: row.lemonsqueezy_order_id,
      })) as AdminEntitlement[];
    },
  });
}

/** KPI aggregati per pagina overview admin */
export function useAdminOverviewStats() {
  const addons = useAdminAddons();
  const entitlements = useAdminEntitlements();

  const stats = (() => {
    if (!addons.data || !entitlements.data) return null;
    const addonMap = new Map(addons.data.map((a) => [a.id, a]));

    // Revenue per addon (esclusi auto-grant a B4T)
    const realEntitlements = entitlements.data.filter((e) => !!e.lemonsqueezy_order_id);
    const revenueByAddon: Record<string, number> = {};
    let totalRevenue = 0;
    for (const e of realEntitlements) {
      const addon = addonMap.get(e.addon_id);
      if (!addon) continue;
      const amount = Number(addon.price_eur ?? 0);
      revenueByAddon[addon.slug] = (revenueByAddon[addon.slug] ?? 0) + amount;
      totalRevenue += amount;
    }

    return {
      totalAddons: addons.data.length,
      activeAddons: addons.data.filter((a) => a.is_active).length,
      totalEntitlements: entitlements.data.length,
      paidEntitlements: realEntitlements.length,
      totalOrgsWithAtLeastOneAddon: new Set(entitlements.data.map((e) => e.organization_id)).size,
      revenueByAddon,
      totalRevenue,
    };
  })();

  return {
    isLoading: addons.isLoading || entitlements.isLoading,
    stats,
  };
}
